import ExpandIcon from '@mui/icons-material/ExpandMore';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { runMultiplexNormalization } from 'api/platform';
import { getHistogramAvailability, getHistogramAvailabilityQueryKey } from 'api/slideMultiplexChannelNormalizations';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import LabelledDropdown from 'components/atoms/Dropdown/LabelledDropdown';
import { ModelTableWithDefaultSelection } from 'components/ModelsTable/ModelTableWithDefaultSelection';
import { modelTypeNormalization } from 'components/Pages/Jobs/inferenceFieldsOptions';
import { JobType, NormalizationJob } from 'interfaces/job';
import { MultiplexHistogramConfig } from 'interfaces/jobs/multiplex/histogramParams';
import {
  MultiplexNormalizationJobParams,
  NormalizationConfig,
  NormalizationMethod,
  NormalizationParamOverrideChannelGroup,
} from 'interfaces/jobs/multiplex/normalizationParams';
import { Model } from 'interfaces/model';
import { Permission } from 'interfaces/permissionOption';
import { MULTIPLEX_STAIN_ID, StainingMethod, StainType } from 'interfaces/stainType';
import { every, flatMap, get, isEmpty, map, omit, size, times, uniq } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { ReactElement, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { humanize } from 'utils/helpers';
import { CasesParams } from 'utils/useCasesParams/types';
import { usePermissions } from 'utils/usePermissions';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import * as yup from 'yup';
import { ActionStepper } from '../ActionStepper';
import { ChannelsController } from '../ChannelsController';
import { JobWithRebuild } from '../JobWithRebuild';
import { LoadFromPreviousJobStep } from '../LoadFromPreviousJobStep';
import { usePreviousJobsAvailability } from '../LoadFromPreviousJobStep/usePreviousJobsAvailability';
import { JobNameAndDescriptionStep } from '../utils';
import { getDnaChannelNames } from './channelSelection';
import {
  buildDnaTilePercentileCaps,
  DNA_TILE_PERCENTILE_CAPS_REQUIRED_MESSAGE,
  isDnaTilePercentileCapsValid,
} from './dnaTilePercentileCaps';
import { MultiplexSelectionAlert } from './MultiplexSelectionAlert';
import HistogramConfig, { defaultHistogramConfig, validationHistogramConfig } from './RunHistogram/HistogramParams';
import { ChannelGroupsOverridesForm } from './RunNormalization/ChannelGroupsOverridesForm';
import {
  defaultChannelNormalizationConfig,
  defaultNormalizationParams,
  normalizationSchema,
} from './RunNormalization/defaults';
import NormalizationParamsForm from './RunNormalization/NormalizationParamsForm';
import { useMultiplexActionSelection } from './useMultiplexActionSelection';

const DNA_TILE_PERCENTILE_CAPS_ARIA_LABEL = 'Apply DNA tile percentile caps';
const SNACK_BAR_KEY_RUN_MULTIPLEX_NORMALIZATION = 'RUN_MULTIPLEX_NORMALIZATION';

export {
  defaultChannelNormalizationConfig,
  defaultNormalizationParams,
  normalizationSchema,
} from './RunNormalization/defaults';

export interface NormalizationFormValues {
  jobName: string;
  jobDescription: string;
  dnaTilePercentileCapsEnabled: boolean;
  configParams: {
    histogramNormalization: MultiplexNormalizationJobParams;
    histogramCreation: MultiplexHistogramConfig;
  };
}

const dnaTilePercentileCapsValidation = (stainTypeOptions: StainType[] = []) =>
  yup
    .object({
      dnaTilePercentileCapsEnabled: yup.boolean(),
      configParams: yup.object({
        histogramNormalization: yup.object({
          method: yup.string(),
          channels: yup.array().of(yup.mixed()),
        }),
      }),
    })
    .test('dna-tile-caps-require-dna-channel', DNA_TILE_PERCENTILE_CAPS_REQUIRED_MESSAGE, function (values) {
      return isDnaTilePercentileCapsValid({
        enabled: values?.dnaTilePercentileCapsEnabled,
        method: values?.configParams?.histogramNormalization?.method,
        channels: values?.configParams?.histogramNormalization?.channels,
        stainTypeOptions,
      });
    });

const buildValidationSchema = (stainTypeOptions: StainType[] = []) => [
  yup.object({}),
  yup.object({
    configParams: yup.object({
      histogramNormalization: yup.object({
        method: yup.string().required('Select a normalization method'),
        channels: yup
          .array()
          .of(yup.mixed())
          .required('Select at least one channel')
          .min(1, 'Select at least one channel'),
        baseParams: yup.object({}).when('method', {
          is: NormalizationMethod.Parametrized,
          then: normalizationSchema,
        }),
        paramOverrideChannelGroups: yup
          .array()
          .of(
            yup.object({
              name: yup.string().required(),
              channels: yup.array().of(yup.mixed()).required(),
              overrides: normalizationSchema,
            })
          )
          .test(
            'unique-channel-values',
            'channel values must be unique across all channel groups',
            function (channelGroupOverrides) {
              const allNestedValues = flatMap(channelGroupOverrides, (override) => override.channels);
              const uniqueValues = new Set(allNestedValues);
              return allNestedValues.length === uniqueValues.size;
            }
          ),
        modelUrl: yup.string().when('method', {
          is: NormalizationMethod.Parametrized,
          then: yup.string().notRequired().nullable(),
          otherwise: yup.string().required('Select a normalization model'),
        }),
      }),
    }),
  }),
  dnaTilePercentileCapsValidation(stainTypeOptions),
  yup.object({}),
  yup.object({
    configParams: yup.object({
      histogramCreation: validationHistogramConfig,
    }),
  }),
  yup.object({}),
];

export interface RunMultiplexNormalizationProps {
  onClose: (event?: any, force?: boolean) => void;
  jobId?: string;
  casesParams: CasesParams;
}

export const RunMultiplexNormalization = (props: RunMultiplexNormalizationProps): ReactElement => {
  const { onClose, casesParams, jobId } = props;
  const {
    effectiveCasesParams,
    infoMessage: multiplexSelectionInfoMessage,
    blockingMessage: multiplexSelectionBlockingMessage,
    isLoadingSelection,
  } = useMultiplexActionSelection(casesParams);
  const multiplexCasesParams = effectiveCasesParams || casesParams;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { stainTypeOptions } = useStainTypeIdToDisplayName();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedJobId, setSelectedJobId] = useState<string>(jobId ?? undefined);
  const [isLoadingSelectedPreviousJob, setIsLoadingSelectedPreviousJob] = useState(false);
  const validationSchema = buildValidationSchema(stainTypeOptions);

  const currentValidationSchema = validationSchema[activeStep];

  const setBasicParams = (basicParams: MultiplexNormalizationJobParams['baseParams']) => {
    reset({
      ...getValues(),
      configParams: {
        ...getValues().configParams,
        histogramNormalization: {
          ...getValues().configParams.histogramNormalization,
          baseParams: basicParams,
        },
      },
    });

    validateStepParams(0);
  };

  const setParamOverrideChannelGroups = (newParamOverrideChannelGroups: NormalizationParamOverrideChannelGroup[]) => {
    reset({
      ...getValues(),
      configParams: {
        ...getValues().configParams,
        histogramNormalization: {
          ...getValues().configParams.histogramNormalization,
          paramOverrideChannelGroups: newParamOverrideChannelGroups,
        },
      },
    });

    validateStepParams(0);
  };

  const setResultDescription = (newResultDescription: string) => {
    reset({
      ...getValues(),
      configParams: {
        ...getValues().configParams,
        histogramNormalization: {
          ...getValues().configParams.histogramNormalization,
          resultDescription: newResultDescription,
        },
      },
    });
  };

  const setInternallyApproved = (newValue: boolean) => {
    reset({
      ...getValues(),
      configParams: {
        ...getValues().configParams,
        histogramNormalization: {
          ...getValues().configParams.histogramNormalization,
          internallyApproved: newValue,
        },
      },
    });
  };

  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>({});
  const [stepErrorMessages, setStepErrorMessages] = useState<Record<number, string[]>>({});

  const methods = useForm<NormalizationFormValues>({
    mode: 'onChange',
    defaultValues: {
      jobName: null,
      jobDescription: null,
      dnaTilePercentileCapsEnabled: false,
      configParams: {
        histogramNormalization: defaultNormalizationParams,
        histogramCreation: defaultHistogramConfig,
      },
    },
    resolver: yupResolver(currentValidationSchema),
  });
  useTrackActionFormDirty(methods.formState.isDirty);

  const {
    reset,
    getValues,
    setValue,
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = methods;

  const checkValidationAndSetIsStepFailed = (stepIndex: number, objectToValidate: Record<string, any>) => {
    validationSchema[stepIndex]
      .validate(objectToValidate, { abortEarly: false })
      .then(() => {
        setIsStepFailed((prev) => ({
          ...prev,
          [stepIndex]: false,
        }));
        setStepErrorMessages((prev) => ({
          ...prev,
          [stepIndex]: [],
        }));
      })
      .catch((error) => {
        console.error(`Validation error in step ${stepIndex}`, error, { objectToValidate });
        setIsStepFailed((prev) => ({
          ...prev,
          [stepIndex]: true,
        }));
        setStepErrorMessages((prev) => ({
          ...prev,
          [stepIndex]: uniq(error?.errors ?? []),
        }));
      });
  };

  const runMultiplexNormalizationMutation = useMutation(runMultiplexNormalization, {
    onError: () => {
      enqueueSnackbar('Error occurred, Multiplex Normalization failed', {
        variant: 'error',
      });
    },
    onSuccess: () => {
      enqueueSnackbar('Multiplex Normalization Started', { variant: 'success' });
    },
    onSettled() {
      closeSnackbar(SNACK_BAR_KEY_RUN_MULTIPLEX_NORMALIZATION);
    },
  });

  const onSubmit: SubmitHandler<NormalizationFormValues> = async (data) => {
    const histogramNormalization = data.configParams?.histogramNormalization;
    if (
      !isDnaTilePercentileCapsValid({
        enabled: data.dnaTilePercentileCapsEnabled,
        method: histogramNormalization?.method,
        channels: histogramNormalization?.channels,
        stainTypeOptions,
      })
    ) {
      setIsStepFailed((prev) => ({ ...prev, 2: true }));
      setStepErrorMessages((prev) => ({
        ...prev,
        2: [DNA_TILE_PERCENTILE_CAPS_REQUIRED_MESSAGE],
      }));
      return;
    }

    const tilePercentileCaps = buildDnaTilePercentileCaps({
      enabled: data.dnaTilePercentileCapsEnabled,
      method: histogramNormalization?.method,
      channels: histogramNormalization?.channels,
      stainTypeOptions,
    });
    const submittedHistogramNormalization = omit(
      {
        ...histogramNormalization,
        tilePercentileCaps,
      },
      tilePercentileCaps ? [] : ['tilePercentileCaps']
    );

    runMultiplexNormalizationMutation.mutate({
      ...multiplexCasesParams,
      jobName: data.jobName,
      jobDescription: data.jobDescription,
      configParams: {
        histogramNormalization: submittedHistogramNormalization,
        histogramCreation: data.configParams?.histogramCreation,
      },
    });

    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>Waiting for Multiplex Normalization to start</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_RUN_MULTIPLEX_NORMALIZATION,
      autoHideDuration: null,
    });

    onClose(undefined, true);
  };

  const validateStepParams = async (stepIndex: number) => {
    checkValidationAndSetIsStepFailed(stepIndex, getValues());
  };

  const onSelectedJobParamChange = (newValue: NormalizationJob) => {
    if (isEmpty(newValue)) {
      setSelectedJobId(undefined);
      reset({
        jobName: null,
        jobDescription: null,
        dnaTilePercentileCapsEnabled: false,
        configParams: {
          histogramNormalization: defaultNormalizationParams,
          histogramCreation: defaultHistogramConfig,
        },
      });
    } else {
      setSelectedJobId(newValue.id);
      reset({
        jobName: newValue?.name,
        jobDescription: newValue?.description,
        dnaTilePercentileCapsEnabled: !isEmpty(newValue?.params?.histogramNormalization?.tilePercentileCaps),
        configParams: newValue?.params,
      });

      validateStepParams(0);
      validateStepParams(2);
      validateStepParams(4);

      if (!('histogramNormalization' in newValue.params)) {
        console.warn('histogramNormalization not found in job params');
      }
      if (!('histogramCreation' in newValue.params)) {
        console.warn('histogramCreation not found in job params');
      }
      if (!('name' in newValue)) {
        console.warn('name not found in job params');
      }
      if (!('description' in newValue)) {
        console.warn('description not found in job params');
      }
    }
  };

  const onSelectModel = (model: Model) => {
    setValue('configParams.histogramNormalization.modelUrl', model.url, { shouldValidate: true });
  };

  const { hasPreviousJobs, isCheckingPreviousJobs } = usePreviousJobsAvailability(JobType.MultiplexNormalization);
  const selectedNormalizationMethod = watch('configParams.histogramNormalization.method');
  const selectedNormalizationChannels = watch('configParams.histogramNormalization.channels');
  const dnaTilePercentileCapsEnabled = watch('dnaTilePercentileCapsEnabled');

  useEffect(() => {
    validateStepParams(2);
  }, [dnaTilePercentileCapsEnabled, selectedNormalizationChannels, selectedNormalizationMethod]);

  const { data: histogramAvailability } = useQuery(
    getHistogramAvailabilityQueryKey(multiplexCasesParams),
    ({ signal }) => getHistogramAvailability(multiplexCasesParams, signal),
    { enabled: !isLoadingSelection && Boolean(multiplexCasesParams) }
  );

  const selectedSlidesAllHaveHistogram =
    !isEmpty(histogramAvailability) && every(histogramAvailability, (slide) => slide.hasHistogram);
  const selectedSlidesHistogramsAllTsm =
    selectedSlidesAllHaveHistogram && every(histogramAvailability, (slide) => slide.maskType === 'tsm');

  const renderHistogramAvailabilityNote = (): ReactElement | undefined => {
    if (!selectedSlidesAllHaveHistogram) {
      return undefined;
    }

    return (
      <Grid item>
        <Alert severity="info">
          Histograms are already available for all selected slides. The latest existing histograms are reused
          automatically unless you change the configuration below.
          {selectedSlidesHistogramsAllTsm
            ? ' The existing histograms are bounded to the Tissue Segmentation Mask.'
            : ''}
        </Alert>
      </Grid>
    );
  };

  const renderMissingFieldsHint = (stepIndex: number): ReactElement | undefined => {
    if (!isStepFailed[stepIndex] || isEmpty(stepErrorMessages[stepIndex])) {
      return undefined;
    }

    return (
      <Typography component="span" variant="caption" sx={{ color: 'error.main' }}>
        Missing: {stepErrorMessages[stepIndex].join(' · ')}
      </Typography>
    );
  };

  const steps = [
    {
      label: 'Reuse previous job settings',
      optional: true,
      hidden: !isCheckingPreviousJobs && !hasPreviousJobs,
      isLoading: isLoadingSelectedPreviousJob,
      content: (
        <LoadFromPreviousJobStep
          jobType={JobType.MultiplexNormalization}
          onSelectedJob={onSelectedJobParamChange}
          onSelectedJobLoadingChange={setIsLoadingSelectedPreviousJob}
          selectedJobId={selectedJobId}
        />
      ),
    },
    {
      label: 'Normalization Configuration',
      subDescription: renderMissingFieldsHint(1),
      content: (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Controller
              control={control}
              name="configParams.histogramNormalization.method"
              render={({ field: { onChange, value } }) => (
                <LabelledDropdown
                  required
                  label="Normalization Method"
                  options={map(Object.values(NormalizationMethod), (method) => ({
                    value: method,
                    text: humanize(method),
                  }))}
                  value={value ?? []}
                  onOptionSelected={(optionValue) => {
                    onChange(optionValue);
                    if (optionValue === NormalizationMethod.Parametrized) {
                      setValue('configParams.histogramNormalization.baseParams', defaultChannelNormalizationConfig, {
                        shouldValidate: true,
                      });
                      setValue('dnaTilePercentileCapsEnabled', false, { shouldValidate: true });
                      setValue('configParams.histogramNormalization.tilePercentileCaps', undefined, {
                        shouldValidate: true,
                      });
                      validateStepParams(2);
                    } else {
                      setValue('configParams.histogramNormalization.baseParams', {}, { shouldValidate: true });
                    }
                  }}
                  error={Boolean(get(errors, 'configParams.histogramNormalization.method'))}
                  helperText={humanize(get(errors, 'configParams.histogramNormalization.method')?.message)}
                />
              )}
            />
          </Grid>
          <Grid item>
            <ChannelsController
              name="configParams.histogramNormalization.channels"
              error={Boolean(get(errors, 'configParams.histogramNormalization.channels'))}
              helperText={get(errors, 'configParams.histogramNormalization.channels')?.message}
              methods={methods}
              casesParams={multiplexCasesParams}
            ></ChannelsController>
          </Grid>
          {watch('configParams.histogramNormalization.method') === NormalizationMethod.Auto && (
            <ModelTableWithDefaultSelection
              modelType={modelTypeNormalization.apiModelValue}
              stainType={MULTIPLEX_STAIN_ID}
              stainingMethod={MULTIPLEX_STAIN_ID as StainingMethod}
              onSelect={onSelectModel}
              selectedModelUrl={watch('configParams.histogramNormalization.modelUrl')}
              onDefaultModelUrlChange={(defaultModelUrl) => {
                setValue('configParams.histogramNormalization.modelUrl', defaultModelUrl, {
                  shouldValidate: true,
                });
                if (defaultModelUrl) {
                  setIsStepFailed((prev) => ({ ...prev, 1: false }));
                }
              }}
              onSelectedModelUrlChange={(newModelUrl) => {
                setValue('configParams.histogramNormalization.modelUrl', newModelUrl, {
                  shouldValidate: true,
                });
              }}
              hasModelUrlError={Boolean(get(errors, 'configParams.histogramNormalization.modelUrl'))}
              modelUrlErrorMessage={humanize(get(errors, 'configParams.histogramNormalization.modelUrl')?.message)}
            />
          )}
          {watch('configParams.histogramNormalization.method') === NormalizationMethod.Parametrized && (
            <>
              <Grid item>
                <Typography variant="h6">Default Configuration</Typography>
                <NormalizationParamsForm
                  normalizationConfig={
                    watch('configParams.histogramNormalization.baseParams') || ({} as NormalizationConfig)
                  }
                  setNormalizationConfig={setBasicParams}
                />
              </Grid>
              <Grid item>
                <Grid item>
                  <Typography variant="h6">Channel Groups Overrides</Typography>
                  <ChannelGroupsOverridesForm
                    channelGroupsOverrides={
                      watch('configParams.histogramNormalization.paramOverrideChannelGroups') ??
                      ([] as NormalizationParamOverrideChannelGroup[])
                    }
                    setChannelGroupsOverrides={setParamOverrideChannelGroups}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      ),
      onNextOrBackClick: () => validateStepParams(1),
    },
    {
      label: 'Additional Configuration',
      optional: true,
      subDescription: renderMissingFieldsHint(2),
      content: (
        <Grid container direction="column" spacing={2} data-testid="additional-configuration-step">
          {watch('configParams.histogramNormalization.method') === NormalizationMethod.Auto && (
            <Grid item>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={watch('dnaTilePercentileCapsEnabled')}
                      disabled={isEmpty(
                        getDnaChannelNames(
                          map(watch('configParams.histogramNormalization.channels'), (channel) => String(channel)),
                          stainTypeOptions
                        )
                      )}
                      inputProps={{ 'aria-label': DNA_TILE_PERCENTILE_CAPS_ARIA_LABEL }}
                      onChange={(_, checked) => {
                        setValue('dnaTilePercentileCapsEnabled', checked, { shouldValidate: true, shouldDirty: true });
                        validateStepParams(2);
                      }}
                    />
                  }
                  label="Apply DNA tile percentile caps"
                />
                <Typography variant="caption" color="text.secondary" sx={{ pl: 1.5 }}>
                  Applies percentile limits to DNA tiles during normalization.
                </Typography>
                {watch('dnaTilePercentileCapsEnabled') &&
                  isEmpty(
                    getDnaChannelNames(
                      map(watch('configParams.histogramNormalization.channels'), (channel) => String(channel)),
                      stainTypeOptions
                    )
                  ) && (
                    <Typography variant="caption" color="error" sx={{ pl: 1.5 }}>
                      {DNA_TILE_PERCENTILE_CAPS_REQUIRED_MESSAGE}
                    </Typography>
                  )}
              </Box>
            </Grid>
          )}
          <Grid item>
            <TextField
              label="Result Description"
              value={watch('configParams.histogramNormalization.resultDescription') ?? ''}
              onChange={(event) => setResultDescription(event.target.value)}
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              label="Internally Approved"
              control={
                <Checkbox
                  checked={watch('configParams.histogramNormalization.internallyApproved') ?? true}
                  onChange={(event) => setInternallyApproved(event.target.checked)}
                />
              }
            />
          </Grid>
        </Grid>
      ),
      onNextOrBackClick: () => validateStepParams(2),
    },
    {
      label: 'Job Name and Description',
      subLabel: activeStep !== 1 && watch('jobName'),
      optional: true,
      content: <JobNameAndDescriptionStep control={control} errors={errors} register={register} />,
    },
    {
      label: 'Histogram Configuration',
      optional: true,
      subDescription: renderMissingFieldsHint(4),
      content: (
        <Grid container direction="column" spacing={2}>
          {renderHistogramAvailabilityNote()}
          <Grid item>
            <HistogramConfig
              histogramConfig={watch('configParams.histogramCreation') ?? ({} as MultiplexHistogramConfig)}
              setHistogramConfig={(histogramConfig) => {
                reset({
                  ...getValues(),
                  configParams: {
                    ...getValues().configParams,
                    histogramCreation: {
                      ...getValues().configParams.histogramCreation,
                      ...histogramConfig,
                    },
                  },
                });

                validateStepParams(4);
              }}
              casesParams={multiplexCasesParams}
            />
          </Grid>
        </Grid>
      ),
      onNextOrBackClick: () => validateStepParams(4),
    },
    {
      label: 'Advanced',
      optional: true,
      content: (
        <Grid container>
          <Grid item xs={2}>
            <TextField
              label="Max Workers"
              type="number"
              InputProps={{ inputProps: { min: 0, max: 16 } }}
              value={watch('configParams.histogramCreation.maxWorkers') ?? 16}
              onChange={(event) =>
                reset({
                  ...getValues(),
                  configParams: {
                    ...getValues().configParams,
                    histogramCreation: {
                      ...getValues().configParams.histogramCreation,
                      maxWorkers: Number(event.target.value),
                    },
                  },
                })
              }
            />
          </Grid>
        </Grid>
      ),
    },
  ];

  const { hasPermission } = usePermissions();
  const canViewJsonJobParams = hasPermission(Permission.ViewJsonJobParams);

  if (isLoadingSelection) {
    return (
      <MultiplexSelectionAlert
        severity="info"
        title="Checking slide selection"
        message="Resolving the multiplex slides for the current case selection."
      />
    );
  }

  if (multiplexSelectionBlockingMessage) {
    return (
      <MultiplexSelectionAlert
        severity="error"
        title="Select slides for this multiplex action"
        message={multiplexSelectionBlockingMessage}
      />
    );
  }

  return (
    <JobWithRebuild jobId={jobId} onSelectedJobParamChange={onSelectedJobParamChange}>
      <Grid container direction="column" spacing={2}>
        {multiplexSelectionInfoMessage ? (
          <Grid item>
            <MultiplexSelectionAlert
              severity="info"
              title="This action runs on slides"
              message={multiplexSelectionInfoMessage}
            />
          </Grid>
        ) : null}
        <Grid item>
          <ActionStepper
            handleSubmit={handleSubmit(onSubmit)}
            validateStep={async () => {
              const allTests = Promise.all(
                times(size(validationSchema), (step) => validationSchema[step].isValid(getValues()))
              );
              return every(allTests, (isValid) => isValid);
            }}
            steps={steps}
            setActiveStepForValidation={setActiveStep}
            isStepFailed={isStepFailed}
            isInitializing={isCheckingPreviousJobs}
            initializingText="Checking for previous jobs…"
          />
        </Grid>
        {canViewJsonJobParams && (
          <Grid item>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandIcon />}>Normalization Params Summary (JSON)</AccordionSummary>
              <AccordionDetails>
                <Typography component="pre">
                  {JSON.stringify(
                    {
                      jobName: watch('jobName'),
                      jobDescription: watch('jobDescription'),
                      configParams: {
                        histogramNormalization: watch('configParams.histogramNormalization'),
                        histogramCreation: watch('configParams.histogramCreation'),
                      },
                    },
                    null,
                    2
                  )}
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}
      </Grid>
    </JobWithRebuild>
  );
};
